/**
 * rruleV1.js
 *
 * Minimal RFC 5545 RRULE subset parser + next-occurrence calculator.
 *
 * This module is intentionally pure:
 * - No HTTP
 * - No Vue
 * - No Intl
 *
 * Supported subset (case-insensitive):
 * - FREQ: DAILY | WEEKLY | MONTHLY | YEARLY
 * - INTERVAL
 * - BYDAY (WEEKLY only)
 * - BYMONTHDAY (MONTHLY only)
 * - BYMONTH (YEARLY only)
 * - COUNT xor UNTIL
 *
 * Timezone semantics:
 * - If anchor is datetime (`scheduled_at`), v1 uses the numeric offset embedded in
 *   the ISO8601 string as a fixed-offset "local calendar".
 * - If anchor is date-only (`scheduled_on`), the caller must provide a local date
 *   boundary (e.g. derived from tzid) and we operate in date space.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const SUPPORTED_KEYS = new Set([
  'FREQ',
  'INTERVAL',
  'BYDAY',
  'BYMONTHDAY',
  'BYMONTH',
  'COUNT',
  'UNTIL'
]);

const BYDAY_TO_WEEKDAY = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6
};

const normalizeParts = (rrule) => {
  const text = String(rrule || '').trim();
  if (!text) return null;

  return text
    .split(';')
    .map((p) => p.trim())
    .filter(Boolean)
    .reduce((acc, part) => {
      const idx = part.indexOf('=');
      if (idx === -1) return acc;
      const key = part.slice(0, idx).trim().toUpperCase();
      const value = part.slice(idx + 1).trim();
      if (!key) return acc;
      acc[key] = value;
      return acc;
    }, {});
};

const parseIntStrict = (value) => {
  const n = Number.parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
};

const parseCsv = (value) =>
  String(value || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/**
 * @param {string} rrule
 * @returns {{
 *   valid: boolean,
 *   reason?: string,
 *   freq?: 'DAILY'|'WEEKLY'|'MONTHLY'|'YEARLY',
 *   interval?: number,
 *   byday?: string[],
 *   bymonthday?: number[],
 *   bymonth?: number[],
 *   count?: number,
 *   until?: string
 * }}
 */
export const parseRruleV1 = (rrule) => {
  const parts = normalizeParts(rrule);
  if (!parts) return { valid: false, reason: 'empty' };

  const unsupported = Object.keys(parts).find((k) => !SUPPORTED_KEYS.has(k));
  if (unsupported)
    return { valid: false, reason: `unsupported_key:${unsupported}` };

  const freq = String(parts.FREQ || '')
    .trim()
    .toUpperCase();
  if (!['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(freq)) {
    return { valid: false, reason: 'unsupported_freq' };
  }

  const intervalRaw =
    parts.INTERVAL !== undefined ? parseIntStrict(parts.INTERVAL) : 1;
  const interval = intervalRaw && intervalRaw > 0 ? intervalRaw : 1;

  // v1 safety constraint:
  // Weekly multi-day sets + INTERVAL > 1 require a stable DTSTART anchor to
  // avoid "week parity" drift. Our v1 model re-anchors at each spawned task
  // instance, so we reject this combination to keep behavior predictable.
  const bydayRaw = parts.BYDAY ? parseCsv(parts.BYDAY) : [];
  if (freq === 'WEEKLY' && interval > 1 && bydayRaw.length > 1) {
    return { valid: false, reason: 'weekly_byday_interval_requires_dtstart' };
  }

  const count = parts.COUNT !== undefined ? parseIntStrict(parts.COUNT) : null;
  const until = parts.UNTIL ? String(parts.UNTIL).trim() : null;
  if (count && until)
    return { valid: false, reason: 'count_until_mutual_exclusion' };
  if (count !== null && count <= 0)
    return { valid: false, reason: 'invalid_count' };

  const byday = parts.BYDAY ? bydayRaw.map((d) => d.toUpperCase()) : [];
  if (byday.length > 0 && freq !== 'WEEKLY')
    return { valid: false, reason: 'byday_requires_weekly' };
  const bydayInvalid = byday.find((d) => BYDAY_TO_WEEKDAY[d] === undefined);
  if (bydayInvalid) return { valid: false, reason: 'invalid_byday' };

  const bymonthday = parts.BYMONTHDAY
    ? parseCsv(parts.BYMONTHDAY)
        .map((v) => parseIntStrict(v))
        .filter((n) => n !== null)
    : [];
  if (bymonthday.length > 0 && freq !== 'MONTHLY')
    return { valid: false, reason: 'bymonthday_requires_monthly' };

  const bymonth = parts.BYMONTH
    ? parseCsv(parts.BYMONTH)
        .map((v) => parseIntStrict(v))
        .filter((n) => n !== null)
    : [];
  if (bymonth.length > 0 && freq !== 'YEARLY')
    return { valid: false, reason: 'bymonth_requires_yearly' };

  return {
    valid: true,
    freq,
    interval,
    byday,
    bymonthday,
    bymonth,
    ...(count !== null ? { count } : {}),
    ...(until ? { until } : {})
  };
};

const parseIsoDateOnly = (value) => {
  if (!value) return null;
  const d = dayjs(String(value).trim(), 'YYYY-MM-DD', true);
  return d.isValid() ? d.startOf('day') : null;
};

const parseIsoInstantUtc = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.utc() : null;
};

const parseOffsetMinutesFromIso = (iso) => {
  const text = String(iso || '').trim();
  if (!text) return null;
  if (text.endsWith('Z')) return 0;
  const m = text.match(/([+-])(\d{2}):?(\d{2})$/);
  if (!m) return null;
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (Number(m[2]) * 60 + Number(m[3]));
};

const isAfter = (candidate, after) => candidate.isAfter(after);

/**
 * Date-only recurrence next occurrence.
 *
 * Caller must provide `afterOn` as a local YYYY-MM-DD derived from tzid.
 */
export const nextOccurrenceOn = ({ rrule, startOn, afterOn }) => {
  const parsed = parseRruleV1(rrule);
  if (!parsed.valid) return null;

  const start = parseIsoDateOnly(startOn);
  const after = parseIsoDateOnly(afterOn);
  if (!start || !after) return null;

  const until = parsed.until
    ? parseIsoDateOnly(parsed.until) ||
      parseIsoInstantUtc(parsed.until)?.startOf('day')
    : null;

  const withinUntil = (d) =>
    !until || d.isSame(until, 'day') || d.isBefore(until, 'day');

  const maybeStopByCount = (idx) =>
    parsed.count !== undefined && idx > parsed.count;

  if (parsed.freq === 'DAILY') {
    const diffDays = after.diff(start, 'day');
    const n = Math.floor(diffDays / parsed.interval) + 1;
    const next = start.add(n * parsed.interval, 'day');
    const idx = n + 1; // 1-based occurrence index.
    if (maybeStopByCount(idx)) return null;
    if (!withinUntil(next)) return null;
    return { scheduled_on: next.format('YYYY-MM-DD') };
  }

  if (parsed.freq === 'WEEKLY') {
    const allowed = (parsed.byday?.length ? parsed.byday : ['MO'])
      .map((d) => BYDAY_TO_WEEKDAY[d])
      .filter((n) => n !== undefined);

    let seen = 0;
    // Iterate in date space; bounded search window for v1.
    for (let i = 0; i <= 366 * 10; i += 1) {
      const candidate = start.add(i, 'day');
      if (!withinUntil(candidate)) return null;
      if (!allowed.includes(candidate.day())) continue;

      const weeksSinceStart = Math.floor(candidate.diff(start, 'day') / 7);
      if (weeksSinceStart % parsed.interval !== 0) continue;

      seen += 1;
      if (maybeStopByCount(seen)) return null;
      if (!isAfter(candidate, after)) continue;
      return { scheduled_on: candidate.format('YYYY-MM-DD') };
    }
    return null;
  }

  if (parsed.freq === 'MONTHLY') {
    const md = parsed.bymonthday?.[0];
    if (!md) return null;

    let seen = 0;
    for (let step = 0; step < 12 * 30; step += 1) {
      const monthStart = start.add(step, 'month').startOf('month');
      const monthsSinceStart =
        monthStart.year() * 12 +
        monthStart.month() -
        (start.year() * 12 + start.month());
      if (monthsSinceStart < 0) continue;
      if (monthsSinceStart % parsed.interval !== 0) continue;

      const candidate = monthStart.date(md);
      if (candidate.month() !== monthStart.month()) continue; // invalid date
      if (!withinUntil(candidate)) return null;

      seen += 1;
      if (maybeStopByCount(seen)) return null;
      if (!isAfter(candidate, after)) continue;
      return { scheduled_on: candidate.format('YYYY-MM-DD') };
    }
    return null;
  }

  if (parsed.freq === 'YEARLY') {
    const bym = parsed.bymonth?.[0];
    if (!bym) return null;

    const dayOfMonth = start.date();
    let seen = 0;
    for (let year = start.year(); year <= start.year() + 200; year += 1) {
      if ((year - start.year()) % parsed.interval !== 0) continue;
      const candidate = dayjs(
        `${year}-${String(bym).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}`,
        'YYYY-MM-DD',
        true
      );
      if (!candidate.isValid()) continue;
      if (!withinUntil(candidate)) return null;

      seen += 1;
      if (maybeStopByCount(seen)) return null;
      if (!isAfter(candidate, after)) continue;
      return { scheduled_on: candidate.format('YYYY-MM-DD') };
    }
    return null;
  }

  return null;
};

/**
 * Datetime recurrence next occurrence.
 *
 * We implement a fixed-offset "local calendar":
 * - Parse offset from `startAtIso` (or `Z`).
 * - Shift UTC instants into a local timeline by adding offset minutes.
 * - Apply calendar math in that shifted timeline.
 * - Shift back to UTC for canonical storage (`scheduled_at` as `...Z`).
 */
export const nextOccurrenceAt = ({ rrule, startAtIso, afterAtIso }) => {
  const parsed = parseRruleV1(rrule);
  if (!parsed.valid) return null;

  const offsetMinutes = parseOffsetMinutesFromIso(startAtIso);
  if (offsetMinutes === null) return null;

  const startUtc = parseIsoInstantUtc(startAtIso);
  const afterUtc = parseIsoInstantUtc(afterAtIso);
  if (!startUtc || !afterUtc) return null;

  const startLocal = startUtc.add(offsetMinutes, 'minute');
  const afterLocal = afterUtc.add(offsetMinutes, 'minute');

  const untilUtc = parsed.until ? parseIsoInstantUtc(parsed.until) : null;
  const untilLocal = untilUtc ? untilUtc.add(offsetMinutes, 'minute') : null;
  const withinUntil = (d) =>
    !untilLocal || d.isSame(untilLocal) || d.isBefore(untilLocal);

  const maybeStopByCount = (idx) =>
    parsed.count !== undefined && idx > parsed.count;

  const toOccurrenceAt = (localCandidate) => {
    const utcCandidate = localCandidate.subtract(offsetMinutes, 'minute');
    return { scheduled_at: utcCandidate.toISOString() };
  };

  if (parsed.freq === 'DAILY') {
    const diffDays = afterLocal.diff(startLocal, 'day');
    const n = Math.floor(diffDays / parsed.interval) + 1;
    const candidate = startLocal.add(n * parsed.interval, 'day');
    const idx = n + 1;
    if (maybeStopByCount(idx)) return null;
    if (!withinUntil(candidate)) return null;
    if (!isAfter(candidate, afterLocal)) return null;
    return toOccurrenceAt(candidate);
  }

  if (parsed.freq === 'WEEKLY') {
    const allowed = (parsed.byday?.length ? parsed.byday : ['MO'])
      .map((d) => BYDAY_TO_WEEKDAY[d])
      .filter((n) => n !== undefined);

    let seen = 0;
    for (let i = 0; i <= 366 * 10; i += 1) {
      const candidate = startLocal.add(i, 'day');
      if (!withinUntil(candidate)) return null;
      if (!allowed.includes(candidate.day())) continue;

      const weeksSinceStart = Math.floor(candidate.diff(startLocal, 'day') / 7);
      if (weeksSinceStart % parsed.interval !== 0) continue;

      seen += 1;
      if (maybeStopByCount(seen)) return null;
      if (!isAfter(candidate, afterLocal)) continue;
      return toOccurrenceAt(candidate);
    }
    return null;
  }

  if (parsed.freq === 'MONTHLY') {
    const md = parsed.bymonthday?.[0];
    if (!md) return null;

    let seen = 0;
    for (let step = 0; step < 12 * 30; step += 1) {
      const monthStart = startLocal.add(step, 'month').startOf('month');
      const monthsSinceStart =
        monthStart.year() * 12 +
        monthStart.month() -
        (startLocal.year() * 12 + startLocal.month());
      if (monthsSinceStart < 0) continue;
      if (monthsSinceStart % parsed.interval !== 0) continue;

      const candidate = monthStart
        .date(md)
        .hour(startLocal.hour())
        .minute(startLocal.minute())
        .second(startLocal.second())
        .millisecond(0);

      if (candidate.month() !== monthStart.month()) continue;
      if (!withinUntil(candidate)) return null;

      seen += 1;
      if (maybeStopByCount(seen)) return null;
      if (!isAfter(candidate, afterLocal)) continue;
      return toOccurrenceAt(candidate);
    }
    return null;
  }

  if (parsed.freq === 'YEARLY') {
    const bym = parsed.bymonth?.[0];
    if (!bym) return null;

    const dayOfMonth = startLocal.date();
    let seen = 0;
    for (
      let year = startLocal.year();
      year <= startLocal.year() + 200;
      year += 1
    ) {
      if ((year - startLocal.year()) % parsed.interval !== 0) continue;
      const candidate = dayjs
        .utc(
          `${year}-${String(bym).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}T00:00:00Z`
        )
        .add(startLocal.hour(), 'hour')
        .add(startLocal.minute(), 'minute')
        .add(startLocal.second(), 'second');

      if (!candidate.isValid()) continue;
      if (!withinUntil(candidate)) return null;

      seen += 1;
      if (maybeStopByCount(seen)) return null;
      if (!isAfter(candidate, afterLocal)) continue;
      return toOccurrenceAt(candidate);
    }
    return null;
  }

  return null;
};
